import React, { useMemo, useRef, useState } from 'react';
import type { FunctionComponent } from 'react';
import { canEdit } from '@trello/boards';
import { memberId } from '@trello/session-cookie';
import { useCurrentBoardBackground } from 'app/src/components/BoardTheme';
import { ViewType } from 'app/src/components/ViewsGenerics';

import styles from './ViewSwitcher.less';
import type { GlyphProps } from '@trello/nachos/icons/list';
import { ListIcon } from '@trello/nachos/icons/list';
import { CalendarIcon } from '@trello/nachos/icons/calendar';
import { TimelineIcon } from '@trello/nachos/icons/timeline';
import { LocationIcon } from '@trello/nachos/icons/location';
import { DashboardIcon } from '@trello/nachos/icons/dashboard';
import { TableIcon } from '@trello/nachos/icons/table';
import { Spinner } from '@trello/nachos/spinner';
import { ViewSwitcherButtons } from './ViewSwitcherButtons';
import { useCurrentView } from './useCurrentView';
import { forTemplate } from '@trello/i18n';
import type { ViewSwitcherBoardQuery } from './ViewSwitcherBoardQuery.generated';
import { useViewSwitcherBoardQuery } from './ViewSwitcherBoardQuery.generated';
import { useViewSwitcherMemberQuery } from './ViewSwitcherMemberQuery.generated';
import useResizeObserver from 'use-resize-observer';

import {
  LazySpotlight,
  LazySpotlightTarget,
  LazySpotlightManager,
} from 'app/src/components/Onboarding';
import { useNewFeature } from 'app/src/components/NewFeature';
import { useFeatureFlag } from '@trello/feature-flag-client';

const format = forTemplate('view_switcher');

export type SwitcherView = Pick<
  NonNullable<
    NonNullable<ViewSwitcherBoardQuery['board']>['prefs']
  >['switcherViews'][0],
  'enabled' | 'viewType'
>;

export type SwitcherView_ViewType = SwitcherView['viewType'];

export interface ViewOption {
  name: SwitcherView_ViewType;
  localizedName: string;
  icon: FunctionComponent<GlyphProps>;
}

export const ViewTypesMapping: Record<ViewType, SwitcherView_ViewType> = {
  board: 'Board',
  table: 'Table',
  calendar: 'Calendar',
  map: 'Map',
  dashboard: 'Dashboard',
  timeline: 'Timeline',
};

export const viewOptions: {
  [key in SwitcherView_ViewType]: ViewOption;
} = {
  Board: {
    name: ViewTypesMapping[ViewType.BOARD],
    localizedName: format('Board'),
    icon: ListIcon,
  },
  Table: {
    name: ViewTypesMapping[ViewType.TABLE],
    localizedName: format('Table'),
    icon: TableIcon,
  },
  Calendar: {
    name: ViewTypesMapping[ViewType.CALENDAR],
    localizedName: format('Calendar'),
    icon: CalendarIcon,
  },
  Timeline: {
    name: ViewTypesMapping[ViewType.TIMELINE],
    localizedName: format('Timeline'),
    icon: TimelineIcon,
  },
  Dashboard: {
    name: ViewTypesMapping[ViewType.DASHBOARD],
    localizedName: format('Dashboard'),
    icon: DashboardIcon,
  },
  Map: {
    name: ViewTypesMapping[ViewType.MAP],
    localizedName: format('Map'),
    icon: LocationIcon,
  },
};

export interface ViewSwitcherProps {
  idBoard: string;
}

export const ViewSwitcher: FunctionComponent<ViewSwitcherProps> = ({
  idBoard,
}) => {
  const { backgroundBrightness } = useCurrentBoardBackground();

  const currentView = useCurrentView();

  const currentViewBoardPrefs = currentView
    ? ViewTypesMapping[currentView]
    : null;

  const {
    data: memberData,
    loading: memberDataLoading,
  } = useViewSwitcherMemberQuery(
    memberId
      ? {
          variables: { memberId },
          waitOn: ['MemberHeader'],
          fetchPolicy: 'cache-only',
        }
      : { skip: true, waitOn: ['None'] },
  );

  const {
    data: boardData,
    loading: boardDataLoading,
  } = useViewSwitcherBoardQuery({
    variables: { boardId: idBoard },
    waitOn: ['CurrentBoardFull'],
    fetchPolicy: 'cache-only',
  });

  const member = memberData?.member;
  const board = boardData?.board;
  const organization = board?.organization || null;

  const loading = boardDataLoading || memberDataLoading;

  const hasViewsFeature = useMemo(
    () => board?.premiumFeatures?.includes('views') ?? false,
    [board?.premiumFeatures],
  );

  const memberCanEditBoard = Boolean(
    board &&
      member &&
      organization &&
      canEdit(member, board, organization, null),
  );

  const defaultSwitcherViews: SwitcherView[] = [
    ViewType.BOARD,
    ViewType.TABLE,
    ViewType.CALENDAR,
    ViewType.TIMELINE,
    ViewType.DASHBOARD,
    ViewType.MAP,
  ].map((viewType) => ({
    enabled: viewType === ViewType.BOARD,
    viewType: ViewTypesMapping[viewType],
  }));

  const { isNewFeature, acknowledgeNewFeature } = useNewFeature(
    'ViewSwitcherSpotlight',
  );

  const spotlightFeatureEnabled = useFeatureFlag(
    'panorama.view-switcher-v-1-spotlight',
    false,
  );

  // TODO see this page, the ResizeObserver stuff here is to avoid visual bugs
  // with the switcher buttons target when there's a banner on the board. Once
  // the long term solution gets implemented we can use that here instead.
  // https://hello.atlassian.net/wiki/spaces/TRELLO/pages/1844465134/Workarounds+for+Dynamic+Spotlight+Positioning
  const [spotlightActive, setSpotlightActive] = useState(true);

  let prevHeight: number | undefined;
  const callback = ({ height }: { height: number | undefined }) => {
    if (height !== prevHeight) {
      setSpotlightActive(false);
      setSpotlightActive(true);
    }
    prevHeight = height;
  };

  const contentRef = useRef<Element>(document.querySelector('#content'));

  useResizeObserver({
    onResize: callback,
    ref: contentRef,
  });

  const switcherViews = useMemo(() => {
    if (hasViewsFeature && board?.prefs?.switcherViews) {
      return board?.prefs?.switcherViews;
    } else {
      return defaultSwitcherViews;
    }
  }, [hasViewsFeature, board?.prefs?.switcherViews, defaultSwitcherViews]);

  const analyticsContainers = useMemo(() => {
    return {
      board: {
        id: idBoard,
      },
      organization: {
        id: board?.idOrganization,
      },
      workspace: {
        id: board?.idOrganization,
      },
      enterprise: {
        id: board?.idEnterprise,
      },
    };
  }, [board?.idEnterprise, board?.idOrganization, idBoard]);

  const switcherButtons = useMemo(
    () => (
      <ViewSwitcherButtons
        switcherViews={switcherViews}
        viewOptions={viewOptions}
        backgroundBrightness={backgroundBrightness}
        currentView={currentViewBoardPrefs}
        hasViewsFeature={hasViewsFeature}
        memberCanEditBoard={memberCanEditBoard}
        idBoard={idBoard}
        analyticsContainers={analyticsContainers}
      />
    ),
    [
      analyticsContainers,
      backgroundBrightness,
      currentViewBoardPrefs,
      hasViewsFeature,
      idBoard,
      memberCanEditBoard,
      switcherViews,
    ],
  );

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={styles.viewSwitcherContainer}>
      {spotlightActive && isNewFeature && spotlightFeatureEnabled ? (
        <LazySpotlightManager>
          <LazySpotlightTarget name="viewSwitcherButtons">
            {switcherButtons}
          </LazySpotlightTarget>
          <LazySpotlight
            actions={[
              {
                onClick: () => {
                  acknowledgeNewFeature({
                    source: 'viewSwitcherButtonsSection',
                    explicit: true,
                  });
                },
                text: format('got-it'),
              },
            ]}
            dialogPlacement="bottom left"
            target="viewSwitcherButtons"
            key="viewSwitcherButtons"
            dialogWidth={275}
          >
            <div className={styles.spotlight}>
              <h1>{format('switch-views-faster-and-get-more-done')}</h1>
              <p>{format("we're-cleaning-up-the-top-of-your-boards")}</p>
            </div>
          </LazySpotlight>
        </LazySpotlightManager>
      ) : (
        switcherButtons
      )}
    </div>
  );
};
